import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CrmClient } from '../crm/crm.model';
import { LazyLocationPickerDirective } from '../shared/location-picker/lazy-location-picker.directive';
import { Party } from '../party/party.model';
import { EquipmentCategory, EquipmentResource } from './field-work.models';

@Component({
  selector: 'app-equipment-resource-create-dialog',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, LazyLocationPickerDirective
  ],
  templateUrl: './equipment-resource-create-dialog.component.html',
  styleUrl: './equipment-resource-create-dialog.component.css'
})
export class EquipmentResourceCreateDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() saving = false;
  @Input() error = '';
  @Input() categories: EquipmentCategory[] = [];
  @Input() suppliers: CrmClient[] = [];
  @Input() parties: Party[] = [];

  // Kept for the legacy label helper; picker options are now server-paged.
  locations: Array<{ id?: number; name?: string; shortDescription?: string }> = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() create = new EventEmitter<EquipmentResource>();

  form: EquipmentResource = this.emptyForm();
  validationError = '';
  readonly ownershipOptions = [{ label: 'Owned', value: 'OWNED' }, { label: 'Rental', value: 'RENTAL' }];
  readonly financialOptions = ['INTERNAL_COST', 'RENTAL_PAYABLE', 'NO_SEPARATE_CHARGE'];
  readonly statusOptions = ['ACTIVE', 'MAINTENANCE', 'OUT_OF_SERVICE', 'RETIRED', 'LOST'];
  readonly conditionOptions = ['GOOD', 'FAIR', 'DAMAGED', 'LOST', 'UNDER_INSPECTION'];
  readonly payeeTypeOptions = ['Company', 'TransportationAgency', 'Laborer'];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) this.reset();
  }

  get categoryOptions(): Array<{ label: string; value: number }> {
    return this.categories.filter(row => row.active !== false && Boolean(row.equipmentCategoryId)).map(row => ({
      label: `${row.categoryName || row.categoryCode}${row.trackingMode ? ` — ${row.trackingMode}` : ''}`,
      value: row.equipmentCategoryId!
    }));
  }

  get supplierOptions(): Array<{ label: string; value: number }> {
    return this.suppliers.filter(row => Boolean(row.id)).map(row => ({
      label: `${row.companyName || row.clientNumber || 'CRM supplier'}${row.clientNumber ? ` — ${row.clientNumber}` : ''}`,
      value: row.id!
    }));
  }

  get payeeOptions(): Array<{ label: string; value: number }> {
    return this.parties.filter(row => row.id?.entityType === this.form.payablePartyType)
      .map(row => ({ label: `${row.entityName} — ${row.id.entityType}`, value: row.id.entityId }));
  }

  get locationOptions(): Array<{ label: string; value: number }> {
    return this.locations.filter(row => Boolean(row.id)).map(row => ({
      label: [row.name, row.shortDescription].filter(Boolean).join(' — '), value: row.id!
    }));
  }

  categoryChanged(categoryId?: number): void {
    const category = this.categories.find(row => row.equipmentCategoryId === categoryId);
    this.form.trackingMode = category?.trackingMode || 'SERIALIZED';
    if (this.form.trackingMode !== 'SERIALIZED') this.form.serialNumber = undefined;
  }

  ownershipChanged(): void {
    if (this.form.ownershipType === 'OWNED') {
      this.form.supplierCrmClientId = undefined;
      this.form.payablePartyType = undefined;
      this.form.payablePartyId = undefined;
      this.form.financialTreatment = 'INTERNAL_COST';
    } else {
      this.form.payablePartyType = 'Company';
      this.form.financialTreatment = 'RENTAL_PAYABLE';
    }
  }

  supplierChanged(supplierId?: number): void {
    const supplier = this.suppliers.find(row => row.id === supplierId);
    if (!supplier) return;
    if (supplier.linkedTransportationAgencyId) {
      this.form.payablePartyType = 'TransportationAgency';
      this.form.payablePartyId = supplier.linkedTransportationAgencyId;
    } else if (supplier.linkedCompanyId) {
      this.form.payablePartyType = 'Company';
      this.form.payablePartyId = supplier.linkedCompanyId;
    }
  }

  reset(): void {
    this.form = this.emptyForm();
    this.validationError = '';
  }

  close(): void {
    if (!this.saving) this.visibleChange.emit(false);
  }

  submit(): void {
    this.validationError = '';
    if (!this.form.resourceCode?.trim() || !this.form.resourceName?.trim() || !this.form.equipmentCategoryId) {
      this.validationError = 'Resource code, name, and equipment category are required.';
      return;
    }
    if (this.form.trackingMode === 'SERIALIZED' && !this.form.serialNumber?.trim()) {
      this.validationError = 'Serial number is required for a serialized resource.';
      return;
    }
    if (this.form.ownershipType === 'RENTAL'
      && (!this.form.supplierCrmClientId || !this.form.payablePartyType || !this.form.payablePartyId)) {
      this.validationError = 'Rental equipment requires a CRM supplier and valid payee.';
      return;
    }
    this.create.emit({
      ...this.form,
      inspectionExpiresOn: this.dateOnly(this.form.inspectionExpiresOn),
      unavailableUntil: this.dateOnly(this.form.unavailableUntil)
    });
  }

  private dateOnly(value?: string | Date): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return value.slice(0, 10);
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private emptyForm(): EquipmentResource {
    return {
      trackingMode: 'SERIALIZED', ownershipType: 'OWNED', capacity: 1,
      operationalStatus: 'ACTIVE', conditionStatus: 'GOOD', financialTreatment: 'INTERNAL_COST'
    };
  }
}
